/**
 * i18n Translation Loader with Lazy Loading
 *
 * Only English (fallback) is loaded at startup.
 * All other languages are loaded dynamically when requested.
 * This dramatically improves initial load time and reduces memory usage.
 */

// Only import English (fallback language) at startup
import en from './en';
import { devError, devWarn } from '@shared/vue/utils/debug';
import { enabledLanguageCodes } from '@shared/core/i18n';

// Widen string literal types so non-English bundles satisfy the shape
// without needing to match exact English string values.
type StringifyLeaves<T> = T extends string
  ? string
  : T extends object
  ? { [K in keyof T]: StringifyLeaves<T[K]> }
  : T;

export type TranslationData = StringifyLeaves<typeof en>;

/**
 * Available language codes.
 *
 * Sourced from `@shared/core/i18n` (`languages.config.ts`). Flip the
 * `enabled` flag there to add or remove a language. Missing message
 * bundles fall back to English at runtime (see `loadLanguageModule`).
 */
export const availableLanguages = enabledLanguageCodes() as readonly string[];

export type AvailableLanguage = string;

/**
 * Codes whose translation bundle is actually wired in `loadLanguageModule`
 * below and safe to persist as a user preference. Codes that are
 * `enabled: true` in the catalog but absent here render with a "Beta"
 * badge in the staff language picker; useful while a freshly-added
 * bundle is waiting on human review.
 *
 * German ships as an overlay (`deOverlay` named export in
 * `./de/index.ts`) deep-merged onto English at module load. Coverage
 * tooling auto-detects this; see `tests/unit/i18n/parity.spec.ts` and
 * `scripts/check-i18n-coverage.mjs`.
 */
export const TRANSLATED_LANGUAGES: readonly string[] = ['en', 'de', 'pl', 'nl'];

export function isLanguageTranslated(language: string): boolean {
  return TRANSLATED_LANGUAGES.includes(language);
}

/**
 * Cache for loaded translations
 */
const translationCache = new Map<string, TranslationData>();

// Pre-load English (fallback)
translationCache.set('en', en);

/**
 * Dynamically import a language translation module.
 *
 * When a new locale is enabled in `@shared/core/i18n/languages.config.ts`,
 * add a `case` here pointing at the corresponding `./<code>` bundle. If
 * the bundle doesn't exist yet (or the dynamic import fails) the loader
 * falls back to English; vue-i18n's per-key fallback then renders the
 * English string for any missing key.
 */
async function loadLanguageModule(language: string): Promise<TranslationData> {
  // Check cache first
  if (translationCache.has(language)) {
    return translationCache.get(language)!;
  }

  let mod: { default: TranslationData } | null = null;
  try {
    switch (language) {
      case 'de':
        mod = await import('./de');
        break;
      case 'pl':
        mod = await import('./pl');
        break;
      case 'nl':
        mod = await import('./nl');
        break;
    }
  } catch (err) {
    devWarn(`Translation bundle for "${language}" not found; using English fallback.`, err);
    mod = null;
  }

  if (mod) {
    translationCache.set(language, mod.default);
    return mod.default;
  }

  // Bundle not wired (yet); fall back to English so the UI keeps working.
  translationCache.set(language, en);
  return en;
}

/**
 * Load a language's translations
 * @param language - Language code to load
 * @returns Promise resolving to translation data
 */
export async function loadLanguage(language: string): Promise<TranslationData> {
  try {
    return await loadLanguageModule(language);
  } catch (error) {
    devError(`Failed to load language ${language}, falling back to English:`, error);
    return en;
  }
}

/**
 * Check if a language is available
 * @param language - Language code to check
 * @returns True if language is available
 */
export function isLanguageAvailable(language: string): boolean {
  return availableLanguages.includes(language);
}

/**
 * Get currently loaded languages
 * @returns Array of loaded language codes
 */
export function getLoadedLanguages(): string[] {
  return Array.from(translationCache.keys());
}

/**
 * Pre-load a language (useful for prefetching)
 * @param language - Language code to preload
 */
export async function preloadLanguage(language: string): Promise<void> {
  if (!translationCache.has(language)) {
    await loadLanguage(language);
  }
}

/**
 * Clear translation cache (useful for testing or memory management)
 * Note: English (fallback) is never cleared
 */
export function clearCache(keepLanguages: string[] = ['en']): void {
  const toKeep = new Map<string, TranslationData>();
  for (const lang of keepLanguages) {
    if (translationCache.has(lang)) {
      toKeep.set(lang, translationCache.get(lang)!);
    }
  }
  translationCache.clear();
  for (const [lang, data] of toKeep) {
    translationCache.set(lang, data);
  }
}

// Default export for vue-i18n eager loading.
// Only includes English initially; pl is lazy-loaded via loadLanguage().
export default {
  en,
};
